import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert } from 'reactstrap';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
import { setup2FA, verify2FASetup, clearError, clearSetupData } from '../../slices/two-factor.slice';
import './TwoFactorSetup.scss';

const TwoFactorSetup = ({ isOpen, toggle }) => {
    const dispatch = useDispatch();
    const { isLoading, setupData, error } = useSelector(state => state.twoFactor);
    const [step, setStep] = useState(1);
    const [verificationCode, setVerificationCode] = useState('');
    const [backupCodesSaved, setBackupCodesSaved] = useState(false);

    useEffect(() => {
        if (isOpen && !setupData) {
            dispatch(setup2FA());
        }
        
        return () => {
            if (!isOpen) {
                dispatch(clearSetupData());
                dispatch(clearError());
                setStep(1);
                setVerificationCode('');
                setBackupCodesSaved(false);
            }
        };
    }, [isOpen, dispatch, setupData]);

    const handleVerifySetup = async (e) => {
        e.preventDefault();
        
        if (!verificationCode.trim()) {
            return;
        }
        
        try {
            await dispatch(verify2FASetup(verificationCode.trim())).unwrap();
            setStep(3);
        } catch (error) {
            console.error('2FA verification failed:', error);
        }
    };

    const handleClose = () => {
        if (step === 3) {
            dispatch(clearSetupData());
            setStep(1);
            setVerificationCode('');
            setBackupCodesSaved(false);
        }
        toggle();
    };

    const downloadBackupCodes = () => {
        if (!setupData?.backupCodes) return;
        
        const content = [
            'BAC Cinema - Two-Factor Authentication Backup Codes',
            '=====================================================',
            '',
            'These backup codes can be used to access your account if you lose access to your authenticator device.',
            'Each code can only be used once. Store them in a safe place.',
            '',
            ...setupData.backupCodes.map((code, index) => `${index + 1}. ${code}`),
            '',
            'Generated on: ' + new Date().toLocaleString()
        ].join('\n');
        
        const element = document.createElement('a');
        const file = new Blob([content], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = 'bac-2fa-backup-codes.txt';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        
        setBackupCodesSaved(true);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="lg" className="two-factor-setup">
            <ModalHeader toggle={handleClose}>
                Set Up Two-Factor Authentication
            </ModalHeader>
            <ModalBody>
                {error && (
                    <Alert color="danger" className="mb-3">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}

                {step === 1 && setupData && (
                    <div>
                        <div className="setup-steps">
                            <div className="step">
                                <span className="step-number">1.</span>
                                Install an authenticator app on your mobile device (Google Authenticator, Microsoft Authenticator, Authy, etc.)
                            </div>
                            <div className="step">
                                <span className="step-number">2.</span>
                                Scan the QR code below with your authenticator app:
                            </div>
                        </div>

                        <div className="qr-code-container">
                            <QRCode value={setupData.qrCodeUrl} size={200} level="M" />
                            <div className="mt-3">
                                <small className="text-muted">
                                    Can't scan? Enter this code manually:
                                </small>
                                <div className="manual-entry">
                                    <code>{setupData.manualEntryKey}</code>
                                    <Button 
                                        size="sm" 
                                        color="link" 
                                        onClick={() => copyToClipboard(setupData.manualEntryKey)}
                                        className="ms-2"
                                    >
                                        <i className="fas fa-copy"></i> Copy
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="backup-codes">
                            <div className="alert-backup-codes">
                                <i className="fas fa-exclamation-triangle"></i>
                                <strong>Important:</strong> Save these backup codes in a secure location. 
                                You can use them to access your account if you lose your authenticator device.
                            </div>
                            
                            <div className="backup-codes-grid">
                                {setupData.backupCodes?.map((code, index) => (
                                    <div key={index} className="backup-code">
                                        {code}
                                    </div>
                                ))}
                            </div>
                            
                            <div className="text-center mt-3">
                                <Button 
                                    color="warning" 
                                    onClick={downloadBackupCodes}
                                    disabled={backupCodesSaved}
                                >
                                    <i className="fas fa-download me-2"></i>
                                    {backupCodesSaved ? 'Backup Codes Downloaded' : 'Download Backup Codes'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div>
                        <div className="step">
                            <span className="step-number">3.</span>
                            Enter the 6-digit code from your authenticator app to complete setup:
                        </div>
                        
                        <Form onSubmit={handleVerifySetup} className="mt-4">
                            <FormGroup className="text-center">
                                <Label for="verificationCode">Verification Code</Label>
                                <Input
                                    type="text"
                                    id="verificationCode"
                                    value={verificationCode}
                                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder="000000"
                                    maxLength={6}
                                    className="verification-input mx-auto"
                                    required
                                />
                            </FormGroup>
                        </Form>
                    </div>
                )}

                {step === 3 && (
                    <div className="text-center">
                        <i className="fas fa-check-circle text-success" style={{fontSize: '4rem'}}></i>
                        <h4 className="text-success mt-3">Two-Factor Authentication Enabled!</h4>
                        <p className="text-muted">
                            Your account is now protected with two-factor authentication. 
                            You'll need to enter a code from your authenticator app when signing in 
                            or performing sensitive actions.
                        </p>
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                {step === 1 && (
                    <>
                        <Button color="secondary" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button 
                            color="primary" 
                            onClick={() => setStep(2)} 
                            disabled={!backupCodesSaved}
                        >
                            Next: Verify Setup
                        </Button>
                    </>
                )}
                
                {step === 2 && (
                    <>
                        <Button color="secondary" onClick={() => setStep(1)}>
                            Back
                        </Button>
                        <Button 
                            color="primary" 
                            onClick={handleVerifySetup} 
                            disabled={isLoading || verificationCode.length !== 6}
                        >
                            {isLoading ? 'Verifying...' : 'Complete Setup'}
                        </Button>
                    </>
                )}
                
                {step === 3 && (
                    <Button color="primary" onClick={handleClose}>
                        Done
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default TwoFactorSetup;